import { Transition } from '@headlessui/react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { CART_PAGE } from '../../constants/page-links';
import { useAppContext } from '../../contexts/AppProvider';
import useProductData from '../../utils/check-latest-stock-for-cart';
import toast from 'react-hot-toast';
/**
 * @description Render memoized cart dropdown component
 * @param {Object} props - Component props
 * @return {React.Component} Memoized cart dropdown component
 */
export const CartDropdown = memo(function MemoizedCartDropdown(props) {
  const [hoverIndex, setHoverIndex] = useState(0);
  const [checkoutResult, setCheckoutResult] = useState({ login_url: '', checkout_url: '', responseReceived: false });
  const [loading, setLoading] = useState(false);
  const { showCartDropdown, setShowCartDropdown } = props;
  const appContext = useAppContext();
  const cartData =
    appContext &&
    Object.prototype.toString.call(appContext) === '[object Object]' &&
    Object.keys(appContext)?.length > 0
      ? appContext.cartData
      : null;
  const cart =
    cartData && Object.prototype.toString.call(cartData) === '[object Object]' && Object.keys(cartData)?.length > 0
      ? cartData.cart
      : null;
  const line_items =
    cart && Object.prototype.toString.call(cart) === '[object Object]' && Object.keys(cart)?.length > 0
      ? cart.line_items
      : null;
  const physical_items =
    line_items &&
    Object.prototype.toString.call(line_items) === '[object Object]' &&
    Object.keys(line_items)?.length > 0
      ? line_items.physical_items?.filter((item) => item.list_price !== 0) || null
      : null;
  const item =
    physical_items && Array.isArray(physical_items) && physical_items?.length > 0
      ? physical_items[physical_items.length - 1]
      : null;

  const data = useStaticQuery(
    graphql`
      {
        allBigCommerceCategories(
          sort: { sort_order: ASC }
          filter: { is_visible: { eq: true }, image_url: { ne: "" }, parent_id: { ne: 0 } }
        ) {
          edges {
            node {
              bigcommerce_id
              custom_url {
                url
              }
              image_url
              name
              parent_id
              sort_order
            }
          }
        }
      }
    `
  );
  const url = item?.url?.replace('https://store.duxiana.com', '');
  const categories = data?.allBigCommerceCategories?.edges || null;

  const onGoCheckout = async () => {
    setLoading(true);
    try {
      const { isOutOfStock } = await useProductData(line_items);
      setLoading(false);
      if (
        appContext &&
        Object.prototype.toString.call(appContext) === '[object Object]' &&
        Object.keys(appContext)?.length > 0 &&
        !isOutOfStock
      ) {
        const { login_url, checkout_url } = await appContext.createCheckoutUrl();
        setCheckoutResult({
          responseReceived: true,
          login_url,
          checkout_url
        });
      }
    } catch (e) {
      setLoading(true);
      toast('Something went wrong!');
    }
  };
  useEffect(() => {
    if (showCartDropdown === true) {
      setTimeout(() => {
        setShowCartDropdown(false);
      }, 5000);
    }
  }, [showCartDropdown]);

  const loginIframeLoaded = () => {
    goToCheckout();
  };

  const goToCheckout = () => {
    if (checkoutResult?.checkout_url) {
      document.getElementsByClassName('header')?.[0].classList.remove('header--expanded');
      setShowCartDropdown(false);
      window.open(checkoutResult.checkout_url, '_self');
    }
  };

  const checkOutLogic = () => {
    if (checkoutResult?.login_url) {
      return <iframe className="hidden" src={checkoutResult.login_url} title="login" ref={loginIframeLoaded} />;
    } else {
      goToCheckout();
    }
  };

  return cartData ? (
    <>
      {checkoutResult.responseReceived && checkOutLogic()}
      <div className="cart-dropdown absolute right-0 top-10 z-10 -mt-[2px] flex h-auto max-h-screen w-[400px] flex-col">
        <Transition
          show={showCartDropdown || false}
          enter="transition-opacity duration-75 "
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="font-outfit border-2 border-black  bg-white">
            {physical_items &&
            Array.isArray(physical_items) &&
            physical_items?.length > 0 &&
            item &&
            Object.prototype.toString.call(item) === '[object Object]' &&
            Object.keys(item)?.length > 0 ? (
              <>
                <div className="flex items-center p-8">
                  {typeof item?.image_url === 'string' && item?.image_url?.length > 0 ? (
                    <Link to={url || '#'}>
                      <img className="h-32 w-32 object-cover" src={item.image_url} alt="" loading="lazy" />
                    </Link>
                  ) : null}

                  <div className="ml-4 flex flex-1 flex-col justify-center pl-2 pr-2">
                    {typeof item?.name === 'string' && item?.name?.length > 0 ? (
                      <Link to={url || '#'}>
                        <span className="text-base-grey-100 text-sm">{item?.name}</span>
                      </Link>
                    ) : null}

                    {item?.options && Array.isArray(item?.options) && item?.options?.length > 0
                      ? item.options.map((option_item, index) => (
                          <span key={index} className="text-base-grey-700 mt-1 text-xs">
                            {option_item.name}: {option_item.value}
                          </span>
                        ))
                      : null}

                    {typeof item?.quantity === 'number' ? (
                      <span className="text-base-grey-700 mt-1 text-xs">Qty: {item.quantity}</span>
                    ) : null}
                    {typeof item?.extended_sale_price === 'number' ? (
                      <span className="mt-1 text-sm text-black">
                        ${item.extended_sale_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="border-t">
                  {typeof cartData?.subtotal_ex_tax === 'number' ? (
                    <div className="flex flex-row justify-between px-8 py-2">
                      <p className="m-0 text-sm uppercase text-[#7c7b7b]">Subtotal:</p>
                      <p className="m-0 text-sm font-bold text-[#7c7b7b]">
                        ${cartData.subtotal_ex_tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  ) : null}

                  <div className="px-8">
                    <button
                      disabled={loading}
                      className={`h-12 w-full bg-black text-sm uppercase text-white ${
                        loading ? 'cursor-not-allowed bg-gray-500' : ''
                      }`}
                      onClick={onGoCheckout}
                    >
                      CHECKOUT
                    </button>

                    <Link to={CART_PAGE}>
                      <button
                        className="mt-4 h-12 w-full  border border-black bg-white  text-sm uppercase text-black"
                        onClick={() => setShowCartDropdown(false)}
                      >
                        Go to cart
                      </button>
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-52 flex-col items-center justify-center border-b">
                <h2 className="text-base-grey-100 text-sm">NO PRODUCTS IN THE CART</h2>
                <Link to="/" className="text-base-grey-700 mt-2 border-b text-xs">
                  Go To Shop
                </Link>
              </div>
            )}

            <div className="flex flex-row gap-x-4 p-8">
              {categories && Array.isArray(categories) && categories?.length > 0 ? (
                <>
                  <div className="flex flex-1 flex-col items-center justify-center">
                    <h3 className="border-b p-1 text-sm font-bold tracking-wider">SHOP BY CATEGORY</h3>

                    {categories.map(({ node }, index) =>
                      typeof node.name === 'string' &&
                      node.name?.length > 0 &&
                      typeof node.custom_url.url === 'string' &&
                      node.custom_url.url?.length > 0 ? (
                        <Link
                          key={node.id || index}
                          to={node.custom_url.url || '#'}
                          className="my-1 text-sm uppercase hover:font-bold"
                          onMouseEnter={() => setHoverIndex(index)}
                          onClick={() => setShowCartDropdown(false)}
                        >
                          {node.name}
                        </Link>
                      ) : null
                    )}
                  </div>

                  <div className="w-40">
                    <img
                      className="h-full w-full object-cover"
                      src={categories[hoverIndex]?.node?.image_url || '#'}
                      alt={categories[hoverIndex]?.node?.name || ''}
                      loading="lazy"
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </Transition>
      </div>
    </>
  ) : null;
});

CartDropdown.propTypes = {
  setShowCartDropdown: PropTypes.func,
  showCartDropdown: PropTypes.bool
};
